import React, {useEffect, useMemo} from "react";
import {Label, Offcanvas, OffcanvasBody, OffcanvasHeader} from "reactstrap";
import NoSearchResult from "../../../../components/Common/NoSearchResult";
import {useFormik} from 'formik';
import * as yup from 'yup';
import {useDispatch, useSelector} from "react-redux";
import {IOffice} from "../../../../store/offices/types";
import {clearOffices, getOffices} from "../../../../store/offices/actions";
import {ICommonOrderUpdate, ICommonOrderUpdateForm} from "../../../../store/commonOrders/types";
import LabelInput from "../../../../components/Common/LabelInput";
import Select from "../../../../components/Common/Select";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {TrafficOrderService} from "../../../../services/traffic_order.service";
import useOffices from "../../../../hooks/useOffices";
import {CommonOrderService} from "../../../../services/common_order.service";
import MultiDatePicker from "../../../../components/Common/MultiDatePicker";
import moment from "moment/moment";

interface IUpdateCommonOrder {
    commonOrderId: number,
    isOpen: boolean,
    toggle: () => void,
}

const UpdateCommonOrder = ({commonOrderId, isOpen, toggle}: IUpdateCommonOrder) => {
    const dispatch = useDispatch();
    const {hasOfficeId} = useOffices();
    const queryClient = useQueryClient();

    const loadingOffices = useSelector((state: any) => state.Offices.loading as boolean);
    const offices = useSelector((state: any) => (state.Offices.data || []) as Array<IOffice>);

    const {
        data: trafficOrders,
        isLoading: loadingOrders,
    } = useQuery({
        queryKey: ["keyTrafficOrders"],
        queryFn: () => TrafficOrderService.getTrafficOrders(),
        retry: false,
    });

    const {
        data: commonOrder,
        isLoading: loadingCommonOrder,
    } = useQuery({
        queryKey: ["keyCommonOrder"],
        queryFn: () => CommonOrderService.getCommonOrder(commonOrderId),
        retry: false,
    });

    const {
        mutate: updateCommonOrder,
        isSuccess: isSuccessUpdate,
        isPending: isPendingUpdate,
    } = useMutation({
        mutationFn: (data: ICommonOrderUpdate) => CommonOrderService.updateCommonOrder(commonOrderId, data),
        mutationKey: ['keyUpdateCommonOrder', commonOrderId]
    });

    useEffect(() => {
        dispatch(getOffices('', {}))

        return () => {
            dispatch(clearOffices())
        };
    }, []);

    useEffect(() => {
        if (isSuccessUpdate) {
            toggle()
            queryClient.invalidateQueries({queryKey: ['keyCommonOrders']})
        }
    }, [isSuccessUpdate]);

    const officesSelect = useMemo(() => (
        offices.map((item: any) => ({
            value: item.id,
            label: item.name
        }))
    ), [offices]);

    const ordersSelect = useMemo(() => (
        trafficOrders?.data.map((item: any) => ({
            value: item.id,
            label: `[${item.id}] | ${item.broker.name}`,
            office_id: item.office_id
        }))
    ), [trafficOrders?.data]);

    const handleDatesChange = (newDates: Date[]) => {
        const sortedDates = [...newDates]
            .map(date => moment(date).format("YYYY-MM-DD"))
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        setFieldValue("dates", sortedDates);
    };

    const validationSchema: yup.Schema<ICommonOrderUpdateForm> = yup.object().shape({
        name: yup.string()
            .min(1, "min 1 character")
            .max(75, "max 75 characters")
            .required("required"),
        orders: yup.array()
            .min(1, "min 1 item")
            .required("required"),
        office_ids: yup.array()
            .required("required"),
        dates: yup.array()
            .min(1, "min 1 item")
            .required("required")
    });

    const serverDates = commonOrder ? commonOrder.dates.map((date: any) => new Date(date)) : [];
    const initialOrders = commonOrder ? commonOrder.data.filter((o: any) => hasOfficeId(o.office_id)) : [];
    const initialOffices = commonOrder ? commonOrder.data.map((o: any) => o.office_id) : [];

    const initialValues: ICommonOrderUpdateForm = {
        name: commonOrder?.name || "",
        orders: initialOrders,
        office_ids: initialOffices,
        dates: commonOrder?.dates || []
    }

    const handleSubmitForm = (value: ICommonOrderUpdateForm) => {
        const assignedOfficeIds = new Set(value.orders.map(order => order.office_id));
        const missingOrders = value.office_ids
            .filter(id => !assignedOfficeIds.has(id))
            .map(id => ({order_id: null, office_id: id}));
        const orders = [...value.orders, ...missingOrders];

        const request = {...commonOrder, name: value.name, data: orders, dates: value.dates}
        updateCommonOrder(request);
    }

    const {
        values,
        handleSubmit,
        handleChange,
        handleBlur,
        errors,
        touched,
        isValid,
        setFieldValue
    } = useFormik({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const handleOrderSelect = (order: any, officeId: number) => {
        const filteredOrders = values.orders.filter(o => o.office_id !== officeId)
        const orders = [...filteredOrders, {order_id: order?.value ?? null, office_id: officeId}]
            .sort((a, b) => a.office_id - b.office_id)

        setFieldValue("orders", orders);
    }

    const handleOfficesSelect = (value: any) => {
        const ids: number[] = value.map((item: any) => item.value)

        if (!ids.some(hasOfficeId)) return

        setFieldValue("office_ids", ids);
        setFieldValue("orders", ids
            .filter(hasOfficeId)
            .map(id => values.orders.find(o => o.office_id === id) ?? {order_id: null, office_id: id})
        );
    }

    const loading = !loadingOffices && !loadingCommonOrder && !loadingOrders

    return (
        <Offcanvas isOpen={isOpen} direction="end" toggle={toggle}>
            <OffcanvasHeader toggle={toggle}> Edit Common Order | {commonOrderId} </OffcanvasHeader>

            {loading ? (
                <OffcanvasBody>
                    <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <LabelInput
                                label="Name"
                                placeholder="Name"
                                name="name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.name}
                                hasError={!!(errors.name && touched.name)}
                                errorText={errors.name}
                            />
                        </div>

                        <div className="mb-3">
                            <label>Select date</label>
                            <MultiDatePicker
                                initialDates={serverDates}
                                onDatesChange={(newDates: Date[]) => handleDatesChange(newDates)}
                            />
                        </div>

                        {values.orders.map(order => {
                            const key = `${order.order_id}_${order.office_id}`;
                            const prefixOffice = offices.find(o => o.id === order.office_id)?.prefix.toUpperCase()

                            const currentOfficeOptions = ordersSelect.filter((o: any) => o.office_id === order.office_id)
                            const currentValue = currentOfficeOptions.find((or: any) =>
                                values.orders.some(o => o.order_id === or.value))

                            return <div key={key} className='mb-3'>
                                <Label>
                                    <span className="fw-bold">[{prefixOffice}]</span> Order:
                                </Label>
                                <Select
                                    isClearable
                                    isSearchable
                                    name="order_id"
                                    label="Order"
                                    placeholder="Order"
                                    options={currentOfficeOptions}
                                    onChange={(value: any) => handleOrderSelect(value, order.office_id)}
                                    value={currentValue}
                                />
                            </div>
                        })}

                        <div className='mb-3'>
                            <Label>Response Offices: </Label>
                            <Select
                                isMulti
                                isSearchable
                                name="office_id"
                                label="Response Office"
                                placeholder="Response Office"
                                options={officesSelect}
                                value={values.office_ids.map(o => officesSelect.find(os => os.value === o))}
                                onChange={(value: any) => handleOfficesSelect(value)}
                            />
                        </div>

                        <button type="submit" className="btn btn-primary w-100" disabled={!isValid || isPendingUpdate}>
                            {isPendingUpdate ? "Loading..." : "Save"}
                        </button>
                    </form>
                </OffcanvasBody>
            ) : (
                <NoSearchResult text="Loading ..."/>
            )}
        </Offcanvas>
    );
};

export default UpdateCommonOrder;